import React from 'react';

import { USE_NONCE_START_VALUE, useNonce } from './use-nonce';

/**
 * Calls specified function and executes the returned promise, or stops the promise pool if
 * returned value is null.
 *
 * Max concurrent promises executed in parallel is specified with poolSize.
 *
 * Returns a function that is used to start the promise pool.
 *
 * @param poolSize Max number of concurrent promises.
 * @param func Function that returns a promise or null if nothing to process.
 */
export const usePromisePool = (poolSize: number, func: () => Promise<void> | null) => {
  const [startNonce, refreshStartNonce] = useNonce();
  const num = React.useRef(0);

  // Starts processing promises up to pool size.
  // When a promise has been resolved/rejected, the useEffect is run again.
  React.useEffect(() => {
    // Don't trig on useNonce default value.
    if (startNonce === USE_NONCE_START_VALUE) {
      return;
    }

    // Start up to poolSize number of concurrent promises.
    while (poolSize > 0 && num.current < poolSize) {
      const promise = func();

      // Stop if no promise is returned.
      if (promise === null) {
        return;
      }

      // Execute promise. When promise is fulfilled/rejected continue with next promise.
      num.current++;
      void promise.finally(() => {
        num.current--;
        refreshStartNonce();
      });
    }
  }, [func, poolSize, refreshStartNonce, startNonce]);

  return { start: refreshStartNonce };
};
